import { useApolloClient } from '@apollo/client';
import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  IconCalendarAlt,
  IconGift,
  IconQuestion,
  IconSpecializationStrategy,
  IconStore,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { type DocumentNode } from 'graphql';
import React, { type FC, type ReactElement, type SVGProps } from 'react';

import {
  ReferralType,
  type Scalars,
} from '../../../../__generated-gql-types__/globalTypes';
import { useReferralsUserQuery } from '../../../../pages/__generated-gql-types__/referrals.generated';
import mxpnl from '../../../../utils/analytics/mixpanel';
import { SCHEDULE_MODAL_EVENTS } from '../../../../utils/analytics/mixpanel-events';
import { categoryUrnToSlug } from '../../../../utils/category';
import { getCoachUrl } from '../../../../utils/coach';
import useCopyToClipboardWithToast from '../../../../utils/hooks/useCopyToClipboardWithToast';
import { getCustomerReferralUrl } from '../../../../utils/url';
import { SessionOption } from '../ScheduleModal';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import type { SchedulingSuccessModalCoachFragment } from './__generated-gql-types__/SchedulingSuccessModal.generated';

interface SchedulingSuccessModalProps
  extends BaseScheduleSubmissionModalLayoutProps {
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  coach: SchedulingSuccessModalCoachFragment;
  refetchQueries?: DocumentNode[];
  scheduledSessionId?: Scalars['CoachingSessionUrn']['output'];
}

export const SchedulingSuccessModal: FC<SchedulingSuccessModalProps> = ({
  setOpen,
  coach,
  selectedTime,
  sessionType,
  isReschedule,
  sessionLength,
  categoryUrn,
  onBackToStart,
  refetchQueries,
  scheduledSessionId,
  ...baseScheduleSubmissionModalLayoutProps
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  const apolloClient = useApolloClient();
  const coachProfileLink = getCoachUrl({
    coachSlug: coach.slug,
    categorySlug: categoryUrnToSlug(categoryUrn ?? ''),
  });
  const { data: userData } = useReferralsUserQuery();
  const referralCode = userData?.user?.applicant?.referralCode;
  const shareableLink = getCustomerReferralUrl(
    referralCode ?? '',
    ReferralType.CUSTOMER_REFER_CUSTOMER,
  );
  const copyLink = useCopyToClipboardWithToast();

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        label: 'Sounds good!',
        size: ButtonSize.LARGE,
        width: ButtonWidth.FULL,
        onClick: () => setOpen(false),
      }}
      coach={coach}
      hideSidebar={!isMd}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      sessionType={sessionType}
      sidebarLocked
      preventScroll
      setOpen={setOpen}
      customSidebar={{
        title: 'Your session was scheduled.',
        subtitle:
          'We’ve got a date — don’t miss it! Missing this call may limit your ability to book future sessions. A calendar invitation has been sent to your email.',
        showAddToCalendar: true,
      }}
      onBackToStart={onBackToStart}
      scheduledSessionId={scheduledSessionId}
      showBackButton={false}
      {...baseScheduleSubmissionModalLayoutProps}
    >
      <div className="flex size-full min-h-fit flex-col items-center justify-start gap-3 bg-leland-gray-hover p-6 text-center text-lg">
        {sessionType === SessionOption.INTRO_CALL ? (
          <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
            <p className="text-sm font-medium text-leland-blue">
              What&apos;s next?
            </p>
            <p className="font-medium">Make the most of your intro call</p>
            <p>
              {`Use this 15-minute call to decide whether ${coach.user.firstName} is a good coaching fit for you. Here are a few tips to help you prepare for the call:`}
            </p>
            <div className="flex flex-col">
              <TextWithIcon
                Icon={IconStore}
                title={
                  <p className="font-medium">
                    Take a look at {coach.user.firstName}’s{' '}
                    <a
                      href={coachProfileLink}
                      className="underline"
                      target="_blank"
                    >
                      profile & coaching offerings
                    </a>
                  </p>
                }
                text="Which options fit your budget and your timeline?"
              />
              <TextWithIcon
                Icon={IconQuestion}
                title={<p className="font-medium">Bring some questions</p>}
                text={`Consider asking about ${coach.user.firstName}’s working style, expertise, or availability.`}
              />
              <TextWithIcon
                Icon={IconSpecializationStrategy}
                title={
                  <p className="font-medium">
                    Come ready to share your goals and challenges
                  </p>
                }
                text={`${coach.user.firstName} will want to learn about you too!`}
              />
            </div>
          </div>
        ) : null}
        {sessionType === SessionOption.INTRO_CALL ? (
          <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
            <p className="font-medium">Looking for help right away?</p>
            <p>
              {`Intro calls aren’t coaching sessions. If you have specific advice-oriented questions for ${coach.user.firstName}, you can book a coaching session instead.`}
            </p>
            <div className="mt-4">
              <Button
                buttonColor={ButtonColor.BLACK}
                label="Book a coaching session"
                LeftIcon={IconCalendarAlt}
                onClick={async () => {
                  await apolloClient.refetchQueries({
                    include: refetchQueries,
                  });
                  onBackToStart();
                  mxpnl.track(
                    SCHEDULE_MODAL_EVENTS.BOOK_TIME_IMMEDIATELY_AFTER_SCHEDULING_INTRO_CALL,
                  );
                }}
              />
            </div>
          </div>
        ) : null}
        {sessionType === SessionOption.COACHING_SESSION ? (
          <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
            <div className="flex flex-row gap-1">
              <IconGift className="size-4 shrink-0 text-leland-primary" />
              <p className="text-sm font-medium text-leland-primary">
                Refer a friend
              </p>
            </div>
            <p className="font-medium">
              {`Get $50 off your next session with ${coach.user.firstName}`}
            </p>
            <p>
              Know someone who could use Leland? When you refer your friends to
              Leland and they purchase, you’ll both get $50 in Leland credit.
            </p>
            <p className="mt-6 font-medium">Your unique referral link</p>
            <div className="flex w-full items-start justify-between rounded-lg border border-leland-gray-stroke bg-white">
              <div className="p-4">
                <p className="line-clamp-1 break-all  text-leland-gray-extra-light">
                  {shareableLink} {/*Enforced at AuthedPageBased */}
                </p>
              </div>

              <button
                type="button"
                className="flex h-full items-center justify-center whitespace-nowrap p-4 text-leland-primary"
                onClick={() => copyLink(shareableLink)}
              >
                Copy link
              </button>
            </div>
          </div>
        ) : null}
        <div className="flex items-center bg-leland-white"></div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};

interface TextWithIconProps {
  Icon: React.FC<SVGProps<SVGSVGElement>>;
  title: ReactElement;
  text: string;
}

const TextWithIcon: FC<TextWithIconProps> = ({ Icon, title, text }) => {
  return (
    <div className="flex flex-row gap-4 py-3">
      {<Icon className="mt-1 size-5 shrink-0" />}
      <div className="flex flex-col">
        {title}
        <p>{text}</p>
      </div>
    </div>
  );
};
